import React, { useEffect, useState, useRef } from "react";
import { useAuthentication } from "../Authentication";
import { useSelector } from "react-redux";
import { selectAccessToken } from "../selectors";
import { TRYON_SERVER_URL } from "../config";
import ThreeDotSpinner from "../components/ThreeDotSpinner";
import deleteIcon from "../assets/deleteIcon.svg";
import { Select } from "flowbite-react";
import NoModels from "../assets/NoModels.svg";
import GradientFont from "../components/GradientFont";
import { selectAuthenticated } from "../selectors";
import AuthenticationRequired from "../components/AuthenticationRequired";
import { useNavigate } from "react-router-dom";
import UploadImage from "../components/UploadImage";
import { AsyncImage } from "loadable-image";
import {
  fetchAllImages,
  deleteExperimentImage,
} from "../Utils/ExperimentImageUtils";
import { AiOutlineDelete } from "react-icons/ai";
import { MdOutlineFileDownload } from "react-icons/md";
import { PiImage, PiUpload } from "react-icons/pi";
import Masonry, {ResponsiveMasonry} from "react-responsive-masonry"
import { LazyLoadImage } from 'react-lazy-load-image-component';

export default function MyImages() {
  const { isAuthenticated } = useAuthentication();
  const accessToken = useSelector(selectAccessToken);
  const [myImages, setMyImages] = useState([]);
  const [fetchingMyImages, setFetchingMyImages] = useState(false);
  const [myImagesFetched, setMyImagesFetched] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [totalImages, setTotalImages] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [previousUrl, setPreviousUrl] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [genderFilter, setGenderFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const authenticated = useSelector(selectAuthenticated);
  const navigate = useNavigate();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadImage, setUploadImage] = useState(null);
  const baseUrl = `${TRYON_SERVER_URL}/api/v1/experiment_image/?`;
  const limit = 20;
  const offset = 0;
  const observerTarget = useRef(null);

  useEffect(() => {
    if (authenticated) {
      if (!myImagesFetched) {
        fetchMyImages(baseUrl, {
          type: typeFilter,
          gender: genderFilter,
          limit: limit,
          offset: offset,
        });
      }
    }
  }, [authenticated]);

  useEffect(() => {
    if (authenticated) {
      fetchMyImages(baseUrl, {
        type: typeFilter,
        gender: genderFilter,
        limit: limit,
        offset: offset,
      });
    }
  }, [typeFilter, genderFilter]);

  useEffect(() => {
    let isSubscribed = true;
    let isFetching = false;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && nextUrl && !fetchingMyImages && isSubscribed && !isFetching) {
          isFetching = true;
          fetchMyImages(nextUrl, {
            type: typeFilter,
            gender: genderFilter
          }).finally(() => {
            isFetching = false;
          });
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '200px'
      }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      isSubscribed = false;
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
      observer.disconnect();
    };
  }, [nextUrl, fetchingMyImages, typeFilter, genderFilter]);

  const fetchMyImages = async (url, params) => {
    setFetchingMyImages(true);
    try {
      const response = await fetchAllImages(
        url,
        accessToken,
        true,
        false,
        params
      );
      if (response.status === "success") {
        setMyImages(prevImages => [...prevImages, ...response.data]);
        setTotalImages(response.count);
        setNextUrl(response.next);
        setPreviousUrl(response.previous);
      } else {
        setError(true);
        setErrorMessage(response.message);
      }
    } catch (err) {
      setError(true);
      setErrorMessage(err.message);
    } finally {
      setFetchingMyImages(false);
      setMyImagesFetched(true);
    }
  };

  const downloadImage = async (imageId, imageUrl) => {
    try {
      console.log("imageUrl:", TRYON_SERVER_URL + "/" + imageUrl);
      const response = await fetch(TRYON_SERVER_URL + "/" + imageUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "image_" + imageId + ".png";
        link.click();
        URL.revokeObjectURL(link.href);
      } else {
        console.log(response);
        setError(true);
        setErrorMessage("Error downloading image!");
      }
    } catch (error) {
      console.log(error);
      setError(true);
      setErrorMessage("Error downloading image!");
    }
  };

  const handleDeleteImage = async (imageId) => {
    const response = await deleteExperimentImage(imageId, accessToken);
    if (response.status === "success") {
      setMyImages((prevImages) =>
        prevImages.filter((image) => image.id !== imageId)
      );
      setTotalImages(totalImages - 1);
      console.log("Image deleted successfully");
    } else {
      setError(true);
      setErrorMessage(response.message);
    }
  };

  const confirmDelete = () => {
    handleDeleteImage(imageToDelete);
    setIsDeleteModalOpen(false);
    setImageToDelete(null);
  };

  const openDeleteModal = (imageId) => {
    setImageToDelete(imageId);
    setIsDeleteModalOpen(true);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    setUploadImage(file);
    setIsUploadModalOpen(true);
  };

  return (
    <div className="flex flex-col h-screen overflow-y-auto p-4 relative">
      <div className="lg:flex lg:justify-between lg:items-center">
        <div className="dark:text-white flex justify-start items-center text-lg md:text-xl text-gray-800">
          <div className="flex justify-start items-center mr-4 text-graphite">
            <PiImage className="w-6 h-6 text-graphite mr-2"/>
            <p>My Images {totalImages > 0 && `(${totalImages})`}</p>
          </div>

          {authenticated && (
            <button 
                className="flex items-center gap-2 px-2 py-1 bg-white/90 text-sm text-primary rounded-lg border border-primary shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer"
                onClick={() => setIsUploadModalOpen(true)}>
                <PiUpload className="w-4 h-4 text-primary"/>
                Upload
            </button>
          )}
        </div>

        {authenticated && (
          <div className="flex space-x-4 flex-wrap items-center mt-4 lg:mt-0">
            <p className="text-sm text-gray-500">Filters:</p>

            <select
              className="text-sm border border-rose-200 bg-white text-gray-500 rounded-lg p-2 focus:ring-rose-300 focus:border-rose-300"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
              }}
            >
              <option className="text-gray-700" value="all">
                All Types
              </option>
              <option className="text-gray-700" value="model">
                Model
              </option>
              <option className="text-gray-700" value="garment">
                Garment
              </option>
              <option className="text-gray-700" value="result">
                Generated
              </option>
            </select>
            <select
              className="text-sm border border-rose-200 bg-white text-gray-500 rounded-lg p-2 focus:ring-rose-300 focus:border-rose-300"
              value={genderFilter}
              onChange={(e) => {
                setGenderFilter(e.target.value);
              }}
            >
              <option className="text-gray-700" value="all">
                All Genders
              </option>
              <option className="text-gray-700" value="male">
                Male
              </option>
              <option className="text-gray-700" value="female">
                Female
              </option>
            </select>
          </div>
        )}
      </div>

      <hr className="border-rose-200/50 mb-4 mt-2" />

      <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 750: 2, 900: 3, 1200: 4 }}>
        <Masonry gutter="16px">
          {myImages.map((image) => (
            <div
              key={image.id}
              className="break-inside-avoid bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 group overflow-hidden cursor-pointer"
            >
              <div className="relative w-full flex items-center justify-center">
                <LazyLoadImage
                  alt={image.name}
                  src={TRYON_SERVER_URL + "/" + image.image_url}
                  effect="opacity"
                  className="w-full h-auto object-cover rounded-xl transform group-hover:scale-105 transition-all duration-500"
                  wrapperClassName="w-full flex items-center justify-center"
                  beforeLoad={() => (
                    <div className="w-full aspect-square bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse flex items-center justify-center">
                      <ThreeDotSpinner />
                    </div>
                  )}
                  afterLoad={() => {}}
                  threshold={100}
                  visibleByDefault={false}
                />

                <div 
                  className="absolute inset-0 flex flex-col justify-between bg-gradient-to-b from-black/50 via-transparent to-black/50 transition-all duration-300 cursor-pointer" 
                  onClick={() => setSelectedImage(image)}
                >
                  <div className="absolute top-2.5 right-2.5 flex justify-end items-center gap-2">
                    <button
                      className="text-white bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-lg p-1.5 shadow-sm hover:shadow-md transition-all duration-300 lg:opacity-0 lg:group-hover:opacity-100 lg:transform lg:hover:scale-105"
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadImage(image.id, image.image_url);
                      }}
                      title="Download Image"
                    >
                      <MdOutlineFileDownload className="w-5 h-5 lg:w-4 lg:h-4"/>
                    </button>
                    <button
                      className="text-white bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-lg p-1.5 shadow-sm hover:shadow-md transition-all duration-300 lg:opacity-0 lg:group-hover:opacity-100 lg:transform lg:hover:scale-105"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteModal(image.id);
                      }}
                      title="Delete Image"
                    >
                      <AiOutlineDelete className="w-5 h-5 lg:w-4 lg:h-4"/>
                    </button>
                  </div>

                  <div className="absolute bottom-0 left-0 p-2 flex flex-wrap justify-start items-center gap-1.5">
                    {image.type !== null && (
                      <div className="text-sm lg:text-xs bg-white/10 backdrop-blur-sm text-white rounded-full px-2 py-0.5 lg:opacity-0 lg:group-hover:opacity-100">
                        {image.type === "result" ? "Generated" : image.type?.charAt(0).toUpperCase() + image.type?.slice(1)}
                      </div>
                    )}
                    {image.gender && (
                      <div className="text-sm lg:text-xs bg-white/10 backdrop-blur-sm text-white rounded-full px-2 py-0.5 lg:opacity-0 lg:group-hover:opacity-100">
                        {image.gender.charAt(0).toUpperCase() + image.gender.slice(1)}
                      </div>
                    )}
                    <div className="text-sm lg:text-xs bg-white/10 backdrop-blur-sm text-white rounded-full px-2 py-0.5 lg:opacity-0 lg:group-hover:opacity-100">
                      {image.access.charAt(0).toUpperCase() + image.access.slice(1)}
                    </div>
                    <div className="text-sm lg:text-xs bg-white/10 backdrop-blur-sm text-white rounded-full px-2 py-0.5 lg:opacity-0 lg:group-hover:opacity-100">
                      {new Date(image.created_on).toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </Masonry>
      </ResponsiveMasonry>

      {fetchingMyImages && (
        <div className="w-full h-[200px] flex items-center justify-center my-4 mb-16 z-50">
          <div className="flex items-center justify-center">
            <ThreeDotSpinner />
          </div>
        </div>
      )}

      {myImagesFetched && myImages.length === 0 && (
        <div className="flex flex-col items-center justify-center h-74 w-full p-4 mb-2 bg-opacity-70 backdrop-blur-lg rounded-lg bg-white/60 border-2 border-rose-100">
          <div className="flex flex-col w-full items-center justify-center ">
            <img
              src={NoModels}
              alt="error"
              className="object-contain h-16 object-center w-16 mb-4"
            />
            <div className="!text-xl font-semibold text-center font-OpenSans ">
              <GradientFont>{"No Images Found!"}</GradientFont>
            </div>
          </div>
          <span className="mx-4 mt-4 !text-neutral-800 text-center font-OpenSans text-sm font-normal leading-normal">
            Uploaded images will be displayed here!
          </span>
        </div>
      )}

      {authenticated == false && (
        <AuthenticationRequired
          message="Please sign in to see your images."
          handleLoginRedirect={() => navigate("/signin")}
        />
      )}

      {!fetchingMyImages && (
        <div className="hidden flex justify-center items-center mt-4 gap-4 absolute bottom-4 left-0 right-0 mx-auto">
          {previousUrl && myImages.length !== 0 && (
            <button
              className="w-32 text-white px-4 py-2 rounded-lg bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700"
              onClick={() => fetchMyImages(previousUrl, {})}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-4 h-4 inline-block"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 19.5 8.25 12l7.5-7.5"
                />
              </svg>
              {" Previous"}
            </button>
          )}
          {nextUrl && myImages.length !== 0 ? (
            <button
              className="w-32 text-white px-4 py-2 rounded-lg bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700"
              onClick={() => fetchMyImages(nextUrl, {})}
            >
              {"Next "}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-4 h-4 inline-block"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8.25 4.5 15.75 12 8.25 19.5"
                />
              </svg>
            </button>
          ) : (
            <></>
          )}
        </div>
      )}

      {error && <div className="text-red-500">{errorMessage}</div>}

      {selectedImage && (
        <div
          className="fixed inset-0 p-4 rounded-lg lg:p-0 flex items-center justify-center bg-black bg-opacity-75 z-50"
          onClick={() => setSelectedImage(null)}
        >
          <div className="max-w-full max-h-full rounded-lg relative">
            <img
              src={TRYON_SERVER_URL + "/" + selectedImage.image_url}
              className="max-w-full max-h-full rounded-lg object-contain"
              alt="Preview"
              style={{ maxHeight: "80vh", maxWidth: "80vw" }}
            />
            <button
              className="absolute top-2 right-2 bg-white rounded-full p-2 shadow-md hover:shadow-lg transition-shadow duration-300"
              onClick={(e) => {
                e.stopPropagation();
                downloadImage(selectedImage.id, selectedImage.image_url);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-5 h-5 text-blue-600"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 16.5V21h18v-4.5M12 3v12m0 0l-3-3m3 3l3-3"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {isDeleteModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg p-6 mx-4 lg:mx-0 w-full lg:w-1/3">
            <h2 className="text-lg font-semibold mb-4">Confirm Deletion</h2>
            <p>Are you sure you want to delete this image?</p>
            <p className="text-sm text-gray-500 mt-2">
              Deleting this image will delete the experiment that has been
              generated using this image.
            </p>
            <div className="flex justify-end mt-4">
              <button
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded mr-2"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="bg-red-500 text-white px-4 py-2 rounded"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {isUploadModalOpen && (
        <UploadImage
          imageType="all"
          allImages={myImages}
          setAllImages={setMyImages}
          closeModal={() => setIsUploadModalOpen(false)}
        />
      )}

      {/* Loading indicator and observer target */}
      <div ref={observerTarget} className="w-full h-20 flex items-center justify-center mt-4">
        {fetchingMyImages && (
          <div className="flex items-center justify-center">
            <div className="animate-pulse h-screen w-full bg-gray-500 border-b-2 border-primary"></div>
          </div>
        )}
      </div>

      {!nextUrl && myImages.length > 0 && (
        <div className="text-center text-gray-500 mt-4">
          No more images to load
        </div>
      )}
    </div>
  );
}
